import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { fetcher } from "api/swr";
import { withCacheBust } from "commons/cdn";
import type { SpineTemplate } from "../../../types/spine-template";
import MyPagination from "components/ui/MyPagination";
import { Button } from "@/components/ui/button";
import { ExportAnimationProgressDialog } from "../../export/components/export-animation/ExportAnimationProgressDialog";
import { ImageUploadProgressDialog } from "../../create-avatar/ImageUploadProgressDialog";
import { toast } from "@/components/ui/use-toast";
import { Loader2, Package } from "lucide-react";
import type { SpineAvatar } from "AdminPanel/SpineAvatars/types";
import { SpineAvatarItem } from "AdminPanel/SpineAvatars/SpineAvatars/components/SpineAvatarItem";
import { buildOutfit } from "../../../lib/outfit";
import { createSpineAvatarUpload } from "../../../lib/createSpineAvatarUpload";
import { spineEditorSelectionHasDynamicSpine } from "../../../lib/hasDynamicSpineSelection";
import { useConfigStore } from "../../../store/configStore";
import { useEditorStore } from "../../../store/editorStore";
import {
  getVapOriginalDimensions,
  getVapPreviewDimensions,
} from "../../../utils/fullscreenType";
import { PinDefaultSetButton } from "./PinDefaultSetButton";
import { RemoveDefaultSetButton } from "./RemoveDefaultSetButton";

const DEFAULT_SET_PAGE_SIZE = 20;

function bustOutfitMedia(avatar: SpineAvatar, t: number): SpineAvatar {
  return {
    ...avatar,
    url: withCacheBust(avatar.url, t),
    videoUrl: withCacheBust(avatar.videoUrl, t),
    videoPreviewUrl: withCacheBust(avatar.videoPreviewUrl, t),
  };
}

export function DefaultSetOutfitPanel() {
  const { tid } = useParams<{ tid: string }>();
  const { templateConfig, loadedRelatedTemplates, spineData } = useConfigStore();
  const {
    selectedSkins,
    selectedToningColors,
    currentAnimation,
    existingSpineVersion,
    spineApp,
    spineCanvas,
    previewMergedStaticStack,
  } = useEditorStore();
  const [isSaving, setIsSaving] = useState(false);
  const [exportProgressOpen, setExportProgressOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [uploadProgressOpen, setUploadProgressOpen] = useState(false);
  const [after, setAfter] = useState<string | null>(null);
  const [afters, setAfters] = useState<string[]>([]);
  const [mediaBust, setMediaBust] = useState(0);

  const goAfter = useCallback((nextAfter: string | null) => {
    if (nextAfter === null) {
      setAfter(null);
      setAfters([]);
      return;
    }
    setAfters((prev) => {
      const index = prev.findIndex((a) => a === nextAfter);
      if (index === -1) {
        return [...prev, nextAfter];
      }
      return prev;
    });
    setAfter(nextAfter);
  }, []);

  useEffect(() => {
    setAfter(null);
    setAfters([]);
  }, [tid]);

  const { data: template } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
    fetcher,
  );
  const fullscreenType = template?.fullscreenType ?? 0;
  const originalDims = getVapOriginalDimensions(fullscreenType);
  const previewDims = getVapPreviewDimensions(fullscreenType);

  const hasDynamic = useMemo(
    () =>
      spineEditorSelectionHasDynamicSpine(
        selectedSkins,
        templateConfig,
        spineData,
        loadedRelatedTemplates,
      ),
    [selectedSkins, templateConfig, spineData, loadedRelatedTemplates],
  );

  const listParams = useMemo(
    () =>
      tid != null && tid !== ""
        ? {
            tid,
            defaultSet: 1,
            limit: DEFAULT_SET_PAGE_SIZE,
            after,
          }
        : null,
    [tid, after],
  );

  const listKey =
    listParams != null
      ? (["/spine-avatars", listParams] as const)
      : null;

  const { data: outfits = [], isLoading, mutate } = useSWR<SpineAvatar[]>(
    listKey,
    fetcher,
  );

  const displayOutfits = useMemo(
    () => (mediaBust ? outfits.map((item) => bustOutfitMedia(item, mediaBust)) : outfits),
    [outfits, mediaBust],
  );

  const nextAfter = displayOutfits[displayOutfits.length - 1]?.id;

  const refreshList = useCallback(() => {
    setMediaBust(Date.now());
    if (after !== null) {
      setAfter(null);
      setAfters([]);
      return;
    }
    if (!listParams) return;
    void mutate(
      () => fetcher(["/spine-avatars", listParams, { cache: "no-store" }]),
      { revalidate: false },
    );
  }, [after, listParams, mutate]);

  const handleSaveAsDefaultSet = async () => {
    if (!tid) {
      toast({
        variant: "destructive",
        title: "失败",
        description: "缺少模板 ID",
      });
      return;
    }

    const outfit = buildOutfit(
      {
        config: templateConfig,
        id: tid,
        spineVersion: existingSpineVersion ?? undefined,
      },
      loadedRelatedTemplates,
      selectedSkins,
      selectedToningColors,
      currentAnimation,
    );
    if (!outfit) {
      toast({
        variant: "destructive",
        title: "失败",
        description: "无法从当前编辑器状态生成搭配数据",
      });
      return;
    }

    setIsSaving(true);
    setExportProgressOpen(true);
    setExportProgress(0);

    try {
      await createSpineAvatarUpload({
        tid,
        drawDataJson: JSON.stringify(outfit),
        selectedSkins,
        templateConfig,
        spineData,
        loadedRelatedTemplates,
        spineApp,
        spineCanvas,
        previewMergedStaticStack,
        fullscreenType,
        defaultSet: 1,
        onExportProgress: setExportProgress,
        onUploadStart: () => {
          setExportProgressOpen(false);
          setExportProgress(0);
          setUploadProgressOpen(true);
        },
      });

      setAfter(null);
      setAfters([]);
      setMediaBust(Date.now());
      await mutate();
      toast({
        title: "已设为套装",
        description: "Avatar 图片生成大约需要 10 秒，之后点刷新即可看到。",
        className: "bg-green-500 text-white",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "保存失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setExportProgressOpen(false);
      setExportProgress(0);
      setUploadProgressOpen(false);
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="default"
          size="sm"
          className="gap-1.5"
          disabled={!tid || isSaving}
          onClick={handleSaveAsDefaultSet}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Package className="h-4 w-4" />
          )}
          设置当前搭配为套装
        </Button>
      </div>

      <ExportAnimationProgressDialog
        open={exportProgressOpen}
        format="webp"
        progress={exportProgress}
        title={
          hasDynamic
            ? `正在生成 VAP 视频（${originalDims.outputWidth}×${originalDims.outputHeight} + ${previewDims.outputWidth}×${previewDims.outputHeight}）`
            : "正在合成静态图"
        }
      />

      <ImageUploadProgressDialog
        open={uploadProgressOpen}
        title={hasDynamic ? "正在上传 VAP 视频" : "正在上传静态图"}
        description={
          hasDynamic
            ? "正在上传原图与缩略图 VAP mp4，请稍候…"
            : "正在上传 webp 封面，请稍候…"
        }
      />

      <div className="space-y-3">
        <div className="flex shrink-0 flex-wrap items-center gap-4">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="w-auto m-0"
            mutate={refreshList}
          />
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">加载中…</p>
        ) : displayOutfits.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无套装</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {displayOutfits.map((avatar) => (
              <SpineAvatarItem
                key={mediaBust ? `${avatar.id}-${mediaBust}` : avatar.id}
                avatar={avatar}
                tid={tid}
                authorFilterLink={false}
                actionsEndSlot={
                  <div className="flex items-center gap-1.5">
                    <PinDefaultSetButton
                      avatarId={avatar.id}
                      onPinned={() => void mutate()}
                    />
                    <RemoveDefaultSetButton
                      avatarId={avatar.id}
                      onRemoved={() => void mutate()}
                    />
                  </div>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
